import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Leaf, MapPin, ShieldCheck } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const ipolliniSections = [
  {
    title: "1. Titolare del trattamento",
    body: ["Il titolare del trattamento è:"],
    details: [
      "ZeroCinqueZero srls",
      "Viale dei Caduti, 109 - Legnago VR",
      "Email: info@zerocinquezero.it",
      "Sito web: www.zerocinquezero.it",
    ],
  },
  {
    title: "2. Dati trattati dall’app",
    body: [
      "iPollini non raccoglie dati personali identificativi dell’utente.",
      "L’unico dato che può essere utilizzato dall’app è la posizione geografica attuale del dispositivo, esclusivamente se l’utente sceglie volontariamente di usare la funzione “Usa posizione”.",
      "La posizione viene utilizzata solo per individuare il comune più vicino e mostrare le informazioni polliniche riferite alla zona selezionata.",
    ],
  },
  {
    title: "3. Uso facoltativo della posizione",
    body: [
      "L’accesso alla posizione è facoltativo.",
      "L’utente può utilizzare iPollini anche senza concedere il permesso di localizzazione, cercando manualmente un comune italiano tramite la funzione di ricerca.",
      "Il consenso alla posizione può essere negato, revocato o modificato in qualsiasi momento dalle impostazioni del dispositivo.",
    ],
  },
  {
    title: "4. Modalità di trattamento della posizione",
    body: ["Quando l’utente utilizza la funzione “Usa posizione”, la posizione del dispositivo:"],
    list: [
      "viene usata esclusivamente per determinare la località più vicina;",
      "non viene registrata;",
      "non viene storicizzata;",
      "non viene associata a un profilo utente;",
      "non viene trasmessa a server esterni per finalità di tracciamento;",
      "non viene condivisa con terze parti;",
      "non viene utilizzata per finalità pubblicitarie;",
      "non viene utilizzata per profilazione.",
    ],
    afterList: ["iPollini non effettua tracciamento dell’utente."],
  },
  {
    title: "5. Dati salvati localmente sul dispositivo",
    body: ["L’app può memorizzare localmente sul dispositivo alcune informazioni funzionali, come:"],
    list: [
      "comune selezionato;",
      "comuni recenti;",
      "preferenze dell’app;",
      "dati pollinici consultati o storicizzati dall’app.",
    ],
    afterList: ["Queste informazioni restano sul dispositivo dell’utente e non vengono utilizzate per identificare la persona."],
  },
  {
    title: "6. Finalità del trattamento",
    body: [
      "L’eventuale utilizzo della posizione ha la sola finalità di consentire all’app di individuare automaticamente il comune più vicino e mostrare le relative informazioni polliniche.",
      "Non sono previste ulteriori finalità di trattamento.",
    ],
  },
  {
    title: "7. Base giuridica",
    body: [
      "L’utilizzo della posizione avviene sulla base del consenso espresso dall’utente tramite il sistema di autorizzazioni del dispositivo.",
      "Il consenso è libero, facoltativo e revocabile in qualsiasi momento.",
    ],
  },
  {
    title: "8. Comunicazione dei dati a terzi",
    body: [
      "iPollini non comunica dati personali dell’utente a terzi.",
      "L’app non vende dati, non condivide dati personali con reti pubblicitarie e non utilizza strumenti di profilazione o tracciamento comportamentale.",
    ],
  },
  {
    title: "9. Servizi di terze parti",
    body: [
      "iPollini può utilizzare servizi tecnici necessari al funzionamento dell’app, ad esempio mappe o fonti informative relative ai dati pollinici.",
      "Tali servizi sono utilizzati esclusivamente per fornire le funzionalità dell’app e non per tracciare l’utente o creare profili personali.",
    ],
  },
  {
    title: "10. Conservazione dei dati",
    body: [
      "iPollini non conserva la posizione dell’utente.",
      "Le eventuali informazioni salvate localmente sul dispositivo rimangono disponibili solo nell’ambiente dell’app e possono essere eliminate rimuovendo l’app dal dispositivo o cancellando i relativi dati locali, se previsto dal sistema operativo.",
    ],
  },
  {
    title: "11. Diritti dell’utente",
    body: [
      "Poiché iPollini non raccoglie dati personali identificativi e non conserva la posizione dell’utente, il titolare non dispone di dati personali direttamente riconducibili all’utente tramite l’app.",
      "Per eventuali richieste relative alla privacy, l’utente può contattare il titolare all’indirizzo indicato nella presente informativa.",
    ],
  },
  {
    title: "12. Minori",
    body: [
      "iPollini non è destinata alla raccolta di dati personali di minori.",
      "L’app ha finalità esclusivamente informative e non richiede registrazione, account utente o inserimento di dati personali.",
    ],
  },
  {
    title: "13. Finalità informative dell’app",
    body: [
      "Le informazioni fornite da iPollini hanno esclusivamente finalità informative.",
      "L’app non fornisce diagnosi, indicazioni terapeutiche o pareri medici e non sostituisce il consulto con un medico o con uno specialista.",
    ],
  },
  {
    title: "14. Modifiche alla Privacy Policy",
    body: [
      "La presente Privacy Policy può essere aggiornata in caso di modifiche tecniche, funzionali o normative.",
      "Gli aggiornamenti saranno pubblicati nella pagina dedicata alla Privacy Policy.",
    ],
  },
];

export default function IPolliniPrivacyPolicyPage() {
  const [pointerStyle, setPointerStyle] = useState({ "--mouse-x": "50%", "--mouse-y": "18%" });

  useEffect(() => {
    document.title = "Privacy Policy iPollini | ZeroCinqueZero";
  }, []);

  const handlePointerMove = (event) => {
    const x = `${Math.round((event.clientX / window.innerWidth) * 100)}%`;
    const y = `${Math.round((event.clientY / window.innerHeight) * 100)}%`;
    setPointerStyle({ "--mouse-x": x, "--mouse-y": y });
  };

  return (
    <div className="app-shell min-h-screen overflow-x-hidden bg-[#050505] text-[#F5F5F7]" style={pointerStyle} onPointerMove={handlePointerMove} data-testid="ipollini-privacy-policy-page">
      <div className="reactive-background" data-testid="ipollini-privacy-reactive-background-layer" aria-hidden="true">
        <span className="reactive-glow reactive-glow-primary" />
        <span className="reactive-glow reactive-glow-accent" />
        <span className="reactive-scanline" />
      </div>

      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl" data-testid="ipollini-privacy-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8 lg:px-12">
          <Link to="/" className="group flex items-center gap-3" data-testid="ipollini-privacy-logo-home-link" aria-label="Torna alla home ZeroCinqueZero">
            <span className="grid h-10 w-10 place-items-center border border-white/15 bg-white text-sm font-black text-black transition-transform duration-300 group-hover:-translate-y-0.5" data-testid="ipollini-privacy-logo-mark">
              050
            </span>
            <span className="hidden text-sm font-semibold uppercase tracking-[0.24em] text-white sm:inline" data-testid="ipollini-privacy-brand-name">
              ZeroCinqueZero
            </span>
          </Link>
          <Button asChild variant="outline" className="h-11 rounded-none border-white/15 bg-transparent px-5 text-white transition-[border-color,transform,background-color] duration-300 hover:-translate-y-0.5 hover:border-white/35 hover:bg-white/10" data-testid="ipollini-privacy-back-home-button">
            <Link to="/"><ArrowLeft size={17} /> Home</Link>
          </Button>
        </div>
      </header>

      <main className="px-5 pb-16 pt-32 sm:px-8 sm:pb-24 sm:pt-40 lg:px-12" data-testid="ipollini-privacy-main-content">
        <div className="mx-auto max-w-5xl">
          <section className="mb-12 border border-white/10 bg-[#0F0F13]/80 p-6 shadow-2xl backdrop-blur-xl sm:p-10" data-testid="ipollini-privacy-hero-card">
            <p className="mb-5 inline-flex items-center gap-3 border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-[#8B8B96]" data-testid="ipollini-privacy-eyebrow">
              <Leaf size={18} weight="fill" className="text-[#0047FF]" /> Informativa privacy app
            </p>
            <h1 className="text-5xl font-black leading-[0.92] tracking-[-0.065em] text-white sm:text-6xl lg:text-7xl" data-testid="ipollini-privacy-title">
              Privacy Policy di iPollini
            </h1>
            <p className="mt-7 max-w-3xl text-base leading-8 text-[#B8B8C2]" data-testid="ipollini-privacy-intro">
              La presente Privacy Policy descrive le modalità di gestione dei dati nell’applicazione iPollini.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="ipollini-privacy-summary-cards">
              <div className="border border-white/10 bg-[#050505] p-4 text-sm leading-6 text-[#B8B8C2]" data-testid="ipollini-privacy-location-card">
                <MapPin size={22} weight="fill" className="mb-3 text-[#FF2E00]" /> Posizione facoltativa, usata solo per individuare il comune più vicino.
              </div>
              <div className="border border-white/10 bg-[#050505] p-4 text-sm leading-6 text-[#B8B8C2]" data-testid="ipollini-privacy-tracking-card">
                <ShieldCheck size={22} weight="fill" className="mb-3 text-[#0047FF]" /> Nessun tracciamento, profilazione o registrazione della posizione.
              </div>
            </div>
          </section>

          <p className="mb-5 border border-[#0047FF]/40 bg-[#0047FF]/10 p-5 text-sm font-semibold uppercase tracking-[0.18em] text-[#DDE6FF]" data-testid="ipollini-privacy-last-updated">
            Ultimo aggiornamento: 06/05/2026
          </p>

          <section className="mb-5 border border-white/10 bg-[#0F0F13]/75 p-6 backdrop-blur-xl sm:p-8" data-testid="ipollini-privacy-app-description">
            <p className="text-base leading-8 text-[#B8B8C2]" data-testid="ipollini-privacy-app-description-text">
              iPollini è un’app informativa che consente di consultare lo stato della diffusione dei principali pollini allergenici in Italia, in base alla località scelta dall’utente.
            </p>
          </section>

          <div className="space-y-5" data-testid="ipollini-privacy-sections-list">
            {ipolliniSections.map((section, index) => (
              <article key={section.title} className="border border-white/10 bg-[#0F0F13]/75 p-6 backdrop-blur-xl sm:p-8" data-testid={`ipollini-privacy-section-${index + 1}`}>
                <h2 className="text-2xl font-bold tracking-[-0.04em] text-white" data-testid={`ipollini-privacy-section-title-${index + 1}`}>{section.title}</h2>
                <div className="mt-5 space-y-4 text-base leading-8 text-[#B8B8C2]" data-testid={`ipollini-privacy-section-body-${index + 1}`}>
                  {section.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                  {section.details && (
                    <div className="border border-white/10 bg-[#050505] p-5 text-white" data-testid={`ipollini-privacy-section-details-${index + 1}`}>
                      {section.details.map((detail) => <p key={detail}>{detail}</p>)}
                    </div>
                  )}
                  {section.list && (
                    <ul className="space-y-3 pl-5" data-testid={`ipollini-privacy-section-list-${index + 1}`}>
                      {section.list.map((item) => <li key={item} className="list-disc marker:text-[#0047FF]">{item}</li>)}
                    </ul>
                  )}
                  {section.afterList?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10 px-5 py-8 text-sm text-[#8B8B96] sm:px-8 lg:px-12" data-testid="ipollini-privacy-footer">
        <div className="mx-auto flex max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="ipollini-privacy-footer-brand">© ZeroCinqueZero — Software house italiana · P.IVA 04830250231</p>
          <Link to="/" className="transition-colors duration-300 hover:text-white" data-testid="ipollini-privacy-footer-home-link">Torna alla home</Link>
        </div>
      </footer>
    </div>
  );
}